import React, { useEffect, useRef, useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useChatWebSocket } from '../../hooks/useChatWebSocket';
import { useOpenClawChat } from '../../hooks/useOpenClawChat';
import { authService } from '../../features/auth/services/authService';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

const useChat = import.meta.env.VITE_CHAT_BACKEND === 'openclaw' ? useOpenClawChat : useChatWebSocket;

const ChatPanel: React.FC = () => {
  const {
    messages,
    isStreaming,
    toolStatus,
    error,
    addUserMessage,
    startStream,
    clearMessages,
    setOpen,
  } = useChatStore();

  // Resolve the logged-in user's email for OpenClaw auto-connect
  const [userEmail, setUserEmail] = useState('');
  useEffect(() => {
    authService.getUserInfo()
      .then((info) => setUserEmail(info.user.email))
      .catch(() => {});
  }, []);

  const { connect, sendMessage, clearHistory } = useChat({ userEmail });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Connect WebSocket when panel mounts
  useEffect(() => {
    connect();
  }, [connect]);

  // Auto-scroll to bottom on new messages, content updates, or status changes
  // Debounced to next animation frame to avoid excessive layout thrashing
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [messages, toolStatus]);

  const handleSend = (text: string) => {
    addUserMessage(text);
    startStream();
    sendMessage(text);
  };

  const handleClear = () => {
    clearMessages();
    clearHistory();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5
        border-b border-neutral-800">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-neutral-300">tesseract</span>
          <span className="text-[10px] text-neutral-600">ai</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="text-[11px] px-2 py-1 rounded text-neutral-600
                hover:text-neutral-400 transition-colors"
              title="Clear conversation"
            >
              clear
            </button>
          )}
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 rounded text-neutral-600
              hover:text-neutral-300 transition-colors"
            title="Close (Esc)"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-3">
        {messages.length === 0 && !isStreaming && (
          <div className="px-4 py-6">
            <div className="text-neutral-500 text-sm space-y-1.5">
              <div className="text-neutral-400">Tesseract AI Assistant</div>
              <div className="text-xs text-neutral-600">
                Ask about platforms, ETL jobs, field mappings, or make API calls to any gateway.
              </div>
            </div>
            <div className="mt-4 space-y-1">
              {['Check Zoom health', 'List all platforms', 'Show recent jobs', 'List Zoom call queues'].map((q) => (
                <button
                  key={q}
                  onClick={() => handleSend(q)}
                  className="block text-xs text-neutral-600 hover:text-blue-400
                    transition-colors"
                >
                  <span className="text-neutral-700 mr-1">&gt;</span>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}

        {/* Transient status (Thinking..., Analyzing...) — below everything */}
        {isStreaming && toolStatus && (
          <div className="px-4 mb-2 flex items-center gap-1.5 text-xs text-neutral-500">
            <span className="text-blue-400 animate-pulse">*</span>
            <span>{toolStatus}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-4 mb-2 text-xs text-red-400">
            <span className="text-red-500 mr-1">!</span>
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ChatInput onSend={handleSend} disabled={isStreaming} />
    </div>
  );
};

export default ChatPanel;
